import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const QUERIES = {
  mobile: '(max-width: 768px)',
  tablet: '(min-width: 769px) and (max-width: 1024px)',
  touch: '(pointer: coarse) and (hover: none)',
  landscapeMobile: '(pointer: coarse) and (hover: none) and (orientation: landscape)',
} as const;

interface ResponsiveState {
  isMobile: boolean;
  isTablet: boolean;
  isTouch: boolean;
  isLandscapeMobile: boolean;
}

function getInitialState(): ResponsiveState {
  if (typeof window === 'undefined') return { isMobile: false, isTablet: false, isTouch: false, isLandscapeMobile: false };
  return {
    isMobile: window.matchMedia(QUERIES.mobile).matches,
    isTablet: window.matchMedia(QUERIES.tablet).matches,
    isTouch: window.matchMedia(QUERIES.touch).matches,
    isLandscapeMobile: window.matchMedia(QUERIES.landscapeMobile).matches,
  };
}

const ResponsiveContext = createContext<ResponsiveState>(getInitialState());

export function ResponsiveProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ResponsiveState>(getInitialState);

  useEffect(() => {
    const mqlMobile = window.matchMedia(QUERIES.mobile);
    const mqlTablet = window.matchMedia(QUERIES.tablet);
    const mqlTouch = window.matchMedia(QUERIES.touch);
    const mqlLandscapeMobile = window.matchMedia(QUERIES.landscapeMobile);

    const onMobile = (e: MediaQueryListEvent) => setState(s => ({ ...s, isMobile: e.matches }));
    const onTablet = (e: MediaQueryListEvent) => setState(s => ({ ...s, isTablet: e.matches }));
    const onTouch = (e: MediaQueryListEvent) => setState(s => ({ ...s, isTouch: e.matches }));
    const onLandscapeMobile = (e: MediaQueryListEvent) => setState(s => ({ ...s, isLandscapeMobile: e.matches }));

    mqlMobile.addEventListener('change', onMobile);
    mqlTablet.addEventListener('change', onTablet);
    mqlTouch.addEventListener('change', onTouch);
    mqlLandscapeMobile.addEventListener('change', onLandscapeMobile);

    return () => {
      mqlMobile.removeEventListener('change', onMobile);
      mqlTablet.removeEventListener('change', onTablet);
      mqlTouch.removeEventListener('change', onTouch);
      mqlLandscapeMobile.removeEventListener('change', onLandscapeMobile);
    };
  }, []);

  return <ResponsiveContext.Provider value={state}>{children}</ResponsiveContext.Provider>;
}

export function useResponsive(): ResponsiveState {
  return useContext(ResponsiveContext);
}
